import * as React from 'react'
import { useMemo, useState } from 'react'

import { PayrollPolicyPeriodTypesPeriodTypeEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import AddToPaymentModal from 'modules/expenses/components/expenses/AddToPaymentModal'
import AddToPayrollModal from 'modules/expenses/components/expenses/AddToPayrollModal'
import RestartApprovalFlowModal from 'modules/expenses/components/expenses/RestartApprovalFlowModal'
import ReviewModal from 'modules/expenses/components/expenses/ReviewModal'
import { expensableResource } from 'modules/expenses/lib/expensables/expensable'

import { Expensable } from './types'

export type ModalState =
  | { type: 'closed' }
  | { type: 'reject'; expensables: Expensable[] }
  | { type: 'add-to-payroll'; expensables: Expensable[] }
  | { type: 'add-to-payment'; expensables: Expensable[] }
  | { type: 'restart-approval'; expensables: Expensable[] }

interface UseBulkActionModalsProps {
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  onReject: (ids: number[], reason?: string) => Promise<unknown>
  onAddToPayroll: (
    period: PayrollPolicyPeriodTypesPeriodTypeEnum,
    ids: number[]
  ) => Promise<unknown>
  onAddToPayment: (ids: number[], dueDate: Date) => Promise<unknown>
  onRestartReview: (ids: number[]) => Promise<unknown>
}

export const useBulkActionModals = ({
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  onReject,
  onAddToPayroll,
  onAddToPayment,
  onRestartReview,
}: UseBulkActionModalsProps) => {
  const [modalState, setModalState] = useState<ModalState>({ type: 'closed' })

  const openRejectModal = (expensables: Expensable[]) => {
    setModalState({ type: 'reject', expensables })
  }

  const openPayrollModal = (expensables: Expensable[]) => {
    setModalState({ type: 'add-to-payroll', expensables })
  }

  const openPaymentModal = (expensables: Expensable[]) => {
    setModalState({ type: 'add-to-payment', expensables })
  }

  const openRestartApprovalModal = (expensables: Expensable[]) => {
    setModalState({ type: 'restart-approval', expensables })
  }

  const closeModal = () => {
    setModalState({ type: 'closed' })
  }

  const handleReject = async (reason?: string) => {
    if (modalState.type !== 'reject') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onReject(ids, reason)
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.list.actions.action_failed'),
      })
    } finally {
      closeModal()
    }
  }

  const handleAddToPayroll = async (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => {
    if (modalState.type !== 'add-to-payroll') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onAddToPayroll(period, ids)
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.list.actions.action_failed'),
      })
    } finally {
      closeModal()
    }
  }

  const handleAddToPayment = async (ids: number[], dueDate: Date) => {
    try {
      await onAddToPayment(ids, dueDate)
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.list.actions.action_failed'),
      })
    } finally {
      closeModal()
    }
  }

  const handleRestartApproval = async () => {
    if (modalState.type !== 'restart-approval') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onRestartReview(ids)
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.list.actions.action_failed'),
      })
    } finally {
      closeModal()
    }
  }

  const selectedCategories = useMemo(() => {
    if (modalState.type !== 'add-to-payroll') return []

    return modalState.expensables
      .map((expensable) => expensableResource(expensable)?.category?.id)
      .filter((id): id is string => Boolean(id))
  }, [modalState])

  const Modals = () => {
    if (modalState.type === 'closed') return null

    switch (modalState.type) {
      case 'reject':
        return <ReviewModal type="reject" onSubmit={handleReject} onClose={closeModal} />

      case 'add-to-payroll':
        return (
          <AddToPayrollModal
            onSubmit={handleAddToPayroll}
            onClose={closeModal}
            currentPeriodOpen={currentPayrollPeriodOpen}
            availableCategories={availablePayrollCategories}
            selectedCategories={selectedCategories}
          />
        )

      case 'add-to-payment':
        return (
          <AddToPaymentModal
            expensables={modalState.expensables}
            onSubmit={handleAddToPayment}
            onClose={closeModal}
          />
        )

      case 'restart-approval':
        return <RestartApprovalFlowModal onSubmit={handleRestartApproval} onClose={closeModal} />
    }
  }

  return {
    openRejectModal,
    openPayrollModal,
    openPaymentModal,
    openRestartApprovalModal,
    Modals,
  }
}
